import api from './api'
import type { AccountSummary } from './mailApi'

// OSPROD-MAIL-CAL-L3: typed client for /api/calendar/* endpoints.

export interface CalDAVConnectArgs {
  url: string
  user: string
  password: string
}

export interface CalDAVConnectResult {
  accountId: number
  calendars: Array<{ uid: string; name: string; color?: string }>
}

// ─── Admin: connected calendars & native-sync health ───────────────
// Shape returned by GET /calendar/admin/accounts (server: @os/calendar
// server/lib/calendarAdminAggregate.ts + server/routes/admin.ts).
// Read-only dashboard for the global "Calendar" module admin section.
// Mirrors the Mail admin overview, plus a per-calendar sync-health table
// (Calendar's account→calendars two-level model). All dates are ISO
// strings (or null where no event has happened yet).

export interface CalendarAdminKpis {
  totalAccounts: number
  activeAccounts: number
  errorAccounts: number
  revokedAccounts: number
  totalCalendars: number
  watchExpiringSoon: number
  neverSynced: number
}

export interface CalendarAdminAccount {
  id: string
  owner: string
  ownerUserId: string
  email: string
  displayName: string | null
  provider: string
  status: string
  lastError: string | null
  calendarCount: number
  selectedCount: number
}

export interface CalendarAdminCalendar {
  id: string
  accountId: string
  accountEmail: string
  owner: string
  summary: string
  accessRole: string
  lastSyncedAt: string | null
  watchExpiresAt: string | null
  hasSyncToken: boolean
  initialSyncDone: boolean
}

export interface CalendarAdminFailure {
  calendarId: string
  calendarSummary: string
  accountEmail: string
  owner: string
  kind: string
  error: string | null
  startedAt: string
}

export interface CalendarAdminOverview {
  kpis: CalendarAdminKpis
  accounts: CalendarAdminAccount[]
  calendars: CalendarAdminCalendar[]
  recentFailures: CalendarAdminFailure[]
}

export const calendarApi = {
  listAccounts: () => api.get<{ accounts: AccountSummary[]; integrationStatus?: string }>('/calendar/accounts'),
  // ── Admin (global "Calendar" module section) ──
  // Read-only overview of every connected calendar account across the
  // workspace, per-calendar sync health, and recent sync failures.
  // Server gates this to admins.
  adminListAccounts: () => api.get<CalendarAdminOverview>('/calendar/admin/accounts'),
  caldavConnect: (args: CalDAVConnectArgs) => api.post<CalDAVConnectResult>('/calendar/accounts/caldav', args),
  setActiveCalendars: (accountId: number, selectedCalendarUids: string[]) =>
    api.patch<{ ok: true }>(`/calendar/accounts/${accountId}`, { selectedCalendarUids }),
  disconnectAccount: (accountId: number) => api.delete<void>(`/calendar/accounts/${accountId}`),
}
